import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { classifyWineSuiteDeviceType } from '@util/dws-device-classification';
import { WsTooltipService } from './ws-tooltip.service';
import { WsTooltipContent, WsTooltipTemplateContext } from './ws-tooltip.types';

@Directive({
  selector: '[wsTooltip]',
})
export class WsTooltipDirective implements OnInit, OnDestroy {
  @Input('wsTooltip') content: WsTooltipContent | TemplateRef<any> | null = null;
  @Input() wsTooltipContext: WsTooltipTemplateContext | null = null;
  @Input() wsTooltipDisabled = false;
  @Input() wsTooltipShowDelay = 200;
  @Input() wsTooltipClass = '';

  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly isTouchMode = classifyWineSuiteDeviceType() === 'tablet';

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private tooltipService: WsTooltipService
  ) {}

  ngOnInit(): void {
    if (this.isTouchMode) {
      this.elementRef.nativeElement.style.cursor = 'pointer';
    }
  }

  ngOnDestroy(): void {
    this.clearHoverTimer();
    if (this.tooltipService.isOpenFor(this.elementRef.nativeElement)) {
      this.tooltipService.closeAll();
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.isTouchMode || this.wsTooltipDisabled) {
      return;
    }
    this.clearHoverTimer();
    this.hoverTimer = setTimeout(() => {
      this.openTooltip();
    }, this.wsTooltipShowDelay);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.isTouchMode) {
      return;
    }
    this.clearHoverTimer();
    this.tooltipService.scheduleClose();
  }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.isTouchMode || this.wsTooltipDisabled) {
      return;
    }
    event.stopPropagation();
    if (this.tooltipService.isOpenFor(this.elementRef.nativeElement)) {
      this.tooltipService.closeAll();
    } else {
      this.openTooltip();
    }
  }

  private openTooltip(): void {
    if (this.content == null) {
      return;
    }
    this.tooltipService.show(
      this.elementRef.nativeElement,
      this.content,
      this.wsTooltipDisabled,
      this.wsTooltipClass,
      this.wsTooltipContext
    );
  }

  private clearHoverTimer(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
  }
}
